/**
 * va-coffre-client-2026.js · LE CHAÎNON MANQUANT : le logo du CLIENT part vers
 * le relais dès qu'on connaît son courriel.
 * __________________________________________________________________________
 *
 * CE QUE ÇA RÈGLE. Jusqu'ici, le logo déposé par un visiteur sur l'accueil
 * restait dans SON navigateur (localStorage "va_logo") et n'allait nulle part.
 * À la commande, le relais cherchait un coffre au nom de son courriel, n'en
 * trouvait aucun, et répondait "aucun-logo". La chaîne n'avait pas d'entrée :
 * le seul module qui parlait au relais (va-logo-fiche-2026.js) est l'outil du
 * VENDEUR, déclenché à la main depuis la console.
 *
 * Ici, on ouvre l'entrée. Dès que les deux morceaux existent en même temps —
 * un logo dans le navigateur ET un courriel valide — on dépose le logo au
 * relais, qui le met EN ATTENTE dans le coffre sous ce courriel. À la création
 * de la commande, le webhook le retrouve et l'attache à la fiche client. Toute
 * la plomberie en aval existe déjà : on ne fait qu'y verser quelque chose.
 *
 * OÙ EST LE COURRIEL. Vérifié dans le navigateur, pas supposé : le panneau
 * « Tes informations » du panier garde ses champs dans l'état de React et
 * n'écrit RIEN dans localStorage ni sessionStorage. Le seul endroit où le
 * courriel existe est le champ lui-même, input[type="email"] (unique sur la
 * page). On lit donc, dans l'ordre : le compte Shopify connecté rendu par le
 * Liquid, la session locale, puis le champ du formulaire.
 *
 * POURQUOI ON ATTEND QUE LE COURRIEL SOIT POSÉ. Un courriel se tape lettre par
 * lettre, et « a@b.ca » est déjà valide au sens de l'expression régulière. Si
 * on partait à chaque frappe, on remplirait le coffre d'adresses tronquées.
 * On part donc sur deux signaux seulement : quand le champ PERD le focus (le
 * client passe au téléphone, ou clique sur payer), ou quand le courriel n'a
 * plus bougé d'un tour de veille à l'autre.
 *
 * POURQUOI UNE SEULE FOIS PAR COUPLE. Le couple (courriel + empreinte du logo)
 * est mémorisé après un dépôt réussi. Sans ça, la veille redéposerait le même
 * logo toutes les trois secondes pendant que le client remplit le formulaire.
 * Le logo change ? L'empreinte change, et un nouveau dépôt part.
 *
 * ÇA NE DOIT JAMAIS COÛTER UNE VENTE. Rien ici n'est sur le chemin de la
 * caisse : aucune attente, aucun blocage, aucune exception qui remonte. Si le
 * relais est en panne, on essaie trois fois et on se tait. Le client passe sa
 * commande exactement comme avant — simplement sans son logo pré-rangé, ce qui
 * est l'état d'aujourd'hui.
 *
 * ON NE JOURNALISE RIEN. Ni le courriel, ni le logo, ni la clé.
 */
(function () {
  "use strict";

  /* Même plafond que va-logo-fiche-2026.js : le relais refuse au-delà. */
  var LIMITE = 3 * 1024 * 1024;

  var CLE_LOGO = "va_logo";
  var CLE_NOM = "va_logo_name";
  var CLE_TON = "va_logo_tone";

  /* Les couples déjà déposés. Survit au rechargement : le client qui revient
     sur le catalogue ne redépose pas ce qui est déjà au coffre. */
  var CLE_MEMOIRE = "va_coffre_deposes";
  var MAX_MEMOIRE = 24;

  /* Trois échecs et on se tait : une panne du relais ne doit pas se traduire
     par une requête toutes les trois secondes pendant tout le parcours. */
  var MAX_ESSAIS = 3;
  var VEILLE = 3000;

  /* Au-delà, fetch(keepalive) refuse le corps. En dessous, le dépôt survit à
     un départ de page — utile si le client file droit vers la caisse. */
  var PLAFOND_KEEPALIVE = 60000;

  var enVol = false;
  var essais = 0;
  var dernierCourriel = "";
  var toursStables = 0;

  function cfg() {
    return window.__VA_CONFIG__ || {};
  }

  /* Exactement la façon de faire de va-logo-fiche-2026.js : l'adresse du
     relais et la clé de site viennent de __VA_CONFIG__, rendu par le Liquid.
     Aucun secret n'est écrit ici. */
  function adresseRelais() {
    var c = cfg();
    var u = String(c.syncWebhook || "").trim();
    if (u) return u;
    var base = String(c.relaisUrl || "").replace(/\/+$/, "");
    return base ? base + "/api/client" : "";
  }

  function valide(courriel) {
    return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
      String(courriel || "").trim()
    );
  }

  /* Du plus sûr au moins sûr : le compte connecté, la session locale, le champ. */
  function courrielConnu() {
    try {
      var duCompte = String(cfg().clientShopifyCourriel || "").trim();
      if (valide(duCompte)) return duCompte.toLowerCase();
    } catch (e) {}
    try {
      var s = JSON.parse(localStorage.getItem("va_session") || "null");
      if (s && valide(s.email)) return String(s.email).trim().toLowerCase();
    } catch (e) {}
    try {
      var champ = document.querySelector('input[type="email"]');
      var v = champ ? String(champ.value || "").trim() : "";
      if (valide(v)) return v.toLowerCase();
    } catch (e) {}
    return "";
  }

  function logoCourant() {
    try {
      var data = localStorage.getItem(CLE_LOGO) || "";
      if (data.slice(0, 11) !== "data:image/") return null;
      /* base64 pèse 4 caractères pour 3 octets : on mesure avant d'envoyer. */
      if (data.length * 0.75 > LIMITE) return null;
      return {
        data: data,
        name: String(localStorage.getItem(CLE_NOM) || "logo").slice(0, 80),
        tone: String(localStorage.getItem(CLE_TON) || "").slice(0, 24)
      };
    } catch (e) {
      return null;
    }
  }

  /* Empreinte courte du logo (djb2). Sert uniquement à reconnaître « le même
     logo qu'il y a trois secondes » — jamais à identifier quelqu'un. */
  function empreinte(texte) {
    var h = 5381;
    for (var i = 0; i < texte.length; i++) {
      h = ((h * 33) ^ texte.charCodeAt(i)) >>> 0;
    }
    return h.toString(36) + "-" + texte.length.toString(36);
  }

  function memoire() {
    try {
      var t = JSON.parse(localStorage.getItem(CLE_MEMOIRE) || "[]");
      return Object.prototype.toString.call(t) === "[object Array]" ? t : [];
    } catch (e) {
      return [];
    }
  }

  function retenir(jeton) {
    try {
      var t = memoire();
      if (t.indexOf(jeton) === -1) t.push(jeton);
      while (t.length > MAX_MEMOIRE) t.shift();
      localStorage.setItem(CLE_MEMOIRE, JSON.stringify(t));
    } catch (e) {}
  }

  function deposer(courriel, logo, jeton) {
    var url = adresseRelais();
    if (!url) return;

    var charge = { data: logo.data, name: logo.name };
    if (logo.tone) charge.tone = logo.tone;

    var corps;
    try {
      corps = JSON.stringify({ email: courriel, logos: [charge] });
    } catch (e) {
      return;
    }

    enVol = true;
    try {
      fetch(url + (url.indexOf("?") !== -1 ? "&" : "?") + "logo=1", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-va-cle": String(cfg().relaisCle || "")
        },
        body: corps,
        keepalive: corps.length < PLAFOND_KEEPALIVE
      })
        .then(function (r) {
          return r.json().catch(function () {
            return {};
          });
        })
        .then(function (j) {
          if (j && j.ok === true) {
            retenir(jeton);
            essais = 0;
          } else {
            essais++;
          }
        })
        .catch(function () {
          essais++;
        })
        .then(function () {
          enVol = false;
        });
    } catch (e) {
      enVol = false;
    }
  }

  /* immediat = on a un signal franc (le champ a perdu le focus, ou Entrée).
     Sinon on exige que le courriel n'ait pas bougé depuis le tour précédent. */
  function tenter(immediat) {
    try {
      if (enVol || essais >= MAX_ESSAIS) return;

      var courriel = courrielConnu();
      if (!courriel) {
        dernierCourriel = "";
        toursStables = 0;
        return;
      }

      var logo = logoCourant();
      if (!logo) return;

      if (!immediat) {
        if (courriel !== dernierCourriel) {
          dernierCourriel = courriel;
          toursStables = 0;
          return;
        }
        toursStables++;
        if (toursStables < 2) return;
      }
      dernierCourriel = courriel;

      var jeton = courriel + "|" + empreinte(logo.data);
      if (memoire().indexOf(jeton) !== -1) return;

      deposer(courriel, logo, jeton);
    } catch (e) {}
  }

  function estChampCourriel(n) {
    return !!(
      n &&
      n.tagName === "INPUT" &&
      String(n.type || "").toLowerCase() === "email"
    );
  }

  function brancher() {
    try {
      /* En capture : le panneau est re-rendu par React, on ne peut pas
         s'accrocher au champ lui-même, il disparaît et revient. */
      document.addEventListener(
        "focusout",
        function (ev) {
          try {
            if (estChampCourriel(ev.target)) tenter(true);
          } catch (e) {}
        },
        true
      );

      /* Entrée valide le formulaire sans passer par une perte de focus. */
      document.addEventListener(
        "keydown",
        function (ev) {
          try {
            if (ev.key === "Enter" && estChampCourriel(ev.target)) tenter(true);
          } catch (e) {}
        },
        true
      );

      /* Filet : couvre le client déjà connecté (courriel connu au chargement)
         et tout chemin de saisie qu'on n'aurait pas prévu. */
      setInterval(function () {
        tenter(false);
      }, VEILLE);

      tenter(true);
    } catch (e) {}
  }

  try {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", brancher);
    } else {
      brancher();
    }
  } catch (e) {}
})();
